import React, { useEffect } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { Prompt, useHistory } from 'react-router';
import { useData, useStatus, isNormal } from 'resift';
import usePrevious from '@sift/resift/usePrevious';
import createRole, { RoleCreateRequest } from 'fetches/rolesAndPermissions/createRole';
import getRoles from 'fetches/rolesAndPermissions/getRoles';
import { ROLE_CREATE_URL, ROLES_URL } from 'components/Router/consts';
import { Theme } from 'styles/ThemeTyping';
import * as Types from 'pages/RolesAndPermissions/typings';

import { Formik, Form } from 'formik';
import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import PageFormErrorMessage from 'components/Page/newComponents/PageFormErrorMessage';
import Button from '@material-ui/core/Button';
import CancelButton from 'components/Page/CancelButton';
import RoleNameAndDescription from 'pages/RolesAndPermissions/molecules/RoleNameAndDescription';

interface Props {
  onClose: () => void;
  onSave: (data: RoleCreateRequest) => void;
}

function RoleCreateForm({ onClose, onSave }: Props) {
  const classes = useStyles();
  const history = useHistory();
  const rolesData = useData(getRoles);
  const roleNames = rolesData ? rolesData.results.map((role: Types.Role) => role.name) : [];
  const createRoleStatus = useStatus(createRole);
  const newRole = useData(createRole);
  const prevNewRole = usePrevious(newRole);

  useEffect(() => {
    if (isNormal(createRoleStatus) && newRole && newRole !== prevNewRole) {
      const roleEditPath = location.pathname.replace(ROLE_CREATE_URL, `${ROLES_URL}/${newRole.id}`);
      history.push(roleEditPath);
    }
  }, [createRoleStatus, newRole]);

  const initialState = {
    name: '',
    description: '',
    membersEditable: false,
    permissionsEditable: false,
    createdAt: '',
    updatedAt: '',
  };

  const handleValidation = (values: any) => {
    const errors = {} as any;

    if (!values.name.length) {
      errors.name = 'Name is required';
    }

    if (!values.description.length) {
      errors.description = 'Description is required';
    }

    if (roleNames.includes(values.name)) {
      errors.name = 'Role name already exists, please choose a different name';
    }

    return errors;
  };

  return (
    <Formik initialValues={initialState} onSubmit={() => {}} validate={handleValidation}>
      {props => {
        const { dirty, values, setFieldValue, errors, resetForm } = props;
        const roleName = _get(values, 'name', '');
        const roleDescription = _get(values, 'description', '');

        const handleSaveAndNext = () => {
          onSave({ name: roleName, description: roleDescription });
          resetForm();
        };

        return (
          <Form className={classes.root}>
            <RoleNameAndDescription
              nameValue={roleName}
              descriptionValue={roleDescription}
              errors={errors}
              setFieldValue={setFieldValue}
            />

            <PageFormToolbar
              rightSide
              dirty={dirty || !_isEmpty(errors)}
              preContent={<PageFormErrorMessage errors={errors} />}
            >
              <CancelButton onClick={onClose} />

              <Button
                onClick={handleSaveAndNext}
                disabled={!dirty || !_isEmpty(errors)}
                variant="contained"
                color="secondary"
              >
                Save and Next
              </Button>
            </PageFormToolbar>
            <Prompt
              when={dirty}
              message={
                'Your work is not saved, or you have errors that need to be addressed! Are you sure you want to leave?'
              }
            />
          </Form>
        );
      }}
    </Formik>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  card: {
    flexGrow: 1,
    maxWidth: 900,
    marginRight: theme.spacing(3),
  },
  subtitle: {
    color: theme.colors.gray2,
  },
}));

export default RoleCreateForm;
